import type { BoxProps } from "@highoutput/ui-components";
import { AspectRatio, Box, Flex, HStack, Icon, Text, VStack } from "@highoutput/ui-components";
import Image from "next/image";
import type { MemberCoreDataFragment } from "~/__generated__/graphql";
import LinkedInIcon from "../icons/linked-in";
import TwitterIcon from "../icons/twitter";
import Certificates from "./certificates";

type CardProps = {
  data: MemberCoreDataFragment;
  renderImage?: (context: MemberCoreDataFragment) => JSX.Element;
} & Omit<BoxProps, "children">;

export default function Card({ data, renderImage, ...props }: CardProps) {
  return (
    <Box {...props}>
      {!!renderImage && renderImage(data)}
      {!renderImage && (
        <AspectRatio
          height={{
            lg: "298.67px",
          }}
          width="full"
          ratio={6 / 5}
          rounded="8px"
          overflow="hidden"
        >
          <Image
            alt={data.name}
            src={data.photo.data?.attributes?.url ?? ""}
            width={data.photo.data?.attributes?.width ?? 600}
            height={data.photo.data?.attributes?.height ?? 400}
            style={{
              objectFit: "cover",
              objectPosition: "top",
            }}
            data-testid={`member.${data.id}.image`}
          />
        </AspectRatio>
      )}

      <Text
        fontWeight={{
          lg: "medium",
        }}
        variant={{
          base: "18/18",
          lg: "20/20",
        }}
        marginTop="24px"
        data-testid={`member.${data.id}.name`}
      >
        {data.name}
      </Text>

      <Text
        variant={{
          base: "14/14",
          lg: "18/18",
        }}
        marginTop={{
          base: "12px",
          lg: "8px",
        }}
        color="secondary.default"
        data-testid={`member.${data.id}.title`}
      >
        {data.title}
      </Text>

      <VStack marginTop="16px" align="start" spacing={0}>
        {!!data.specialty?.major && (
          <Text
            variant={{
              base: "16/24",
              lg: "18/28",
            }}
            letterSpacing={{
              base: "0.02em",
              lg: "unset",
            }}
            color="rgba(13,48,80,0.6)"
            data-testid={`member.${data.id}.specialty.major`}
          >
            Major specialization: {data.specialty?.major}
          </Text>
        )}

        {!!data.specialty?.minor && (
          <Text
            variant={{
              base: "16/24",
              lg: "18/28",
            }}
            color="rgba(13,48,80,0.6)"
            data-testid={`member.${data.id}.specialty.minor`}
          >
            Minor specialization: {data.specialty?.minor}
          </Text>
        )}
      </VStack>

      <Certificates data={data.certificates ?? []} mt="24px" />

      <HStack marginTop="24px" spacing="20px">
        <Flex
          as="a"
          target="_blank"
          rel="noreferrer noopener"
          href={data.socials?.twitter ?? "https://twitter.com/CoachingAdvante"}
        >
          <Icon as={TwitterIcon} fill="#1DA1F2" width="24px" height="24px" />
        </Flex>

        <Flex
          as="a"
          target="_blank"
          rel="noreferrer noopener"
          href={data.socials?.linkedin ?? "https://www.linkedin.com/company/advantecoaching"}
        >
          <Icon as={LinkedInIcon} fill="#0A66C2" width="24px" height="24px" />
        </Flex>
      </HStack>
    </Box>
  );
}
